import './projects.scss';
import projectList from '../../data/projects.js';
import { populateProjects } from '../../utils/populateProjects.js';
import { gsap } from 'gsap';
import { WindowManager, dragElement } from 'dom-window-manager';

const stickerWindowManager = new WindowManager(1);

export default function projects() {
  const projects = document.getElementById('projects');
  if (!projects) return;

  // "New!" sticker - sits at the top-right of the projects section
  const sticker = document.createElement('div');
  sticker.setAttribute('id', 'new');
  sticker.innerHTML = `<img alt="New! sticker" src="../../assets/new.svg" />`;
  projects.appendChild(sticker);

  dragElement(sticker);
  sticker.style.zIndex = stickerWindowManager.base;
  sticker.addEventListener('mousedown', () => {
    sticker.style.zIndex = stickerWindowManager.moveOnTop();
  });

  let stickerSpinFrame = 0;

  const updateStickerSpin = () => {
    stickerSpinFrame = 0;

    const scrollRange =
      document.documentElement.scrollHeight - window.innerHeight;
    const scrollProgress = scrollRange > 0 ? window.scrollY / scrollRange : 0;

    sticker.style.setProperty(
      '--new-sticker-spin',
      `${scrollProgress * 1440}deg`
    );
  };

  const scheduleStickerSpin = () => {
    if (stickerSpinFrame !== 0) return;

    stickerSpinFrame = window.requestAnimationFrame(updateStickerSpin);
  };

  scheduleStickerSpin();
  window.addEventListener('scroll', scheduleStickerSpin, { passive: true });
  window.addEventListener('resize', scheduleStickerSpin);

  // Title
  const projectsTitle = document.createElement('div');
  projectsTitle.classList.add('wavy');
  projectsTitle.innerHTML = /* html */ `
    ${'PORTFOLIO'
      .split('')
      .map(
        (letter, index) => `<span style="--i:${index + 1}">${letter}</span> `
      )
      .join('')}
  `;
  projects.appendChild(projectsTitle);

  // Container and Projects Population
  const projectsContainer = document.createElement('div');
  projectsContainer.id = 'projects-container';
  projects.appendChild(projectsContainer);

  // Populate Projects
  populateProjects(0, projectList.length, projectsContainer);

  const cards = Array.from(
    projectsContainer.querySelectorAll<HTMLElement>('.project-card')
  );
  const extraCards = cards.slice(3);

  if (extraCards.length > 0) {
    projectsContainer.classList.add('projects-collapsed');
    extraCards.forEach((card) => card.classList.add('project-card--hidden'));

    const showMore = document.createElement('button');
    showMore.id = 'show-more';
    showMore.type = 'button';
    showMore.setAttribute('aria-controls', 'projects-container');
    showMore.setAttribute('aria-expanded', 'false');
    showMore.textContent = 'Show more projects ↓';

    showMore.addEventListener('click', () => {
      const isExpanded = showMore.getAttribute('aria-expanded') === 'true';

      projectsContainer.classList.toggle('projects-collapsed', isExpanded);
      extraCards.forEach((card) =>
        card.classList.toggle('project-card--hidden', isExpanded)
      );
      showMore.setAttribute('aria-expanded', String(!isExpanded));
      showMore.textContent = isExpanded
        ? 'Show more projects ↓'
        : 'Show fewer projects ↑';
    });

    projects.appendChild(showMore);
  }

  // Info Box
  const more = document.createElement('p');
  more.id = 'more';
  more.innerHTML =
    'The list gets updated regularly with new projects. Check back again soon!';
  projects.appendChild(more);

  // Card Animation
  // Set the initial state of the cards
  gsap.set(cards, { opacity: 0, y: 50 });

  const observer = new IntersectionObserver(
    (entries, observer) => {
      // Create an array of all the cards that are now intersecting
      const intersectingCards = entries
        .filter((entry) => entry.isIntersecting)
        .map((entry) => entry.target);

      // If any cards have intersected, animate them
      if (intersectingCards.length > 0) {
        gsap.to(intersectingCards, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          stagger: 0.15, // 0.15s delay between each card's animation
        });

        // Unobserve the cards that were just animated
        intersectingCards.forEach((card) => observer.unobserve(card));
      }
    },
    {
      threshold: 0.15, // Start when 15% of the card is visible
    }
  );

  // Tell the observer to watch each card
  cards.forEach((card) => {
    observer.observe(card);
  });
}
